/* eslint-disable no-unused-vars */
import { UserMinus, UserPlus, Users, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import type { CindroOrgInvite, CindroOrgMember } from '@cindro/contracts';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatAbsoluteDate, formatRelativeDate } from '../utils/formatters';
import {
  listOrganizationMembers,
  listOrganizationInvites,
  updateOrganizationMemberRole,
  removeOrganizationMember,
  revokeOrganizationInvite,
} from '../api';
import { initialsFrom } from '../utils/formatters';

function MembersPage() {
  const { session } = useSession();
  const { activeOrganization, activePage } = useWorkspace();
  const { confirm, toast } = useFeedback();

  const [orgMembers, setOrgMembers] = useState<CindroOrgMember[]>([]);
  const [orgInvites, setOrgInvites] = useState<CindroOrgInvite[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersLoadError, setMembersLoadError] = useState(false);
  const [memberActionError, setMemberActionError] = useState('');

  async function handleLoadMembers() {
    if (!session || !activeOrganization) return;
    setMembersLoading(true);
    setMembersLoadError(false);
    try {
      const [memberRows, inviteRows] = await Promise.all([
        listOrganizationMembers(session.accessToken, activeOrganization.id),
        listOrganizationInvites(session.accessToken, activeOrganization.id).catch(() => [] as CindroOrgInvite[])
      ]);
      setOrgMembers(memberRows);
      setOrgInvites(inviteRows);
    } catch {
      setMembersLoadError(true);
    } finally {
      setMembersLoading(false);
    }
  }

  useEffect(() => {
    if (activePage === 'members' && session && activeOrganization) {
      void handleLoadMembers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, activeOrganization?.id, session?.accessToken]);

  async function handleUpdateMemberRole(memberId: string, role: 'admin' | 'member' | 'viewer') {
    if (!session || !activeOrganization) return;
    setMemberActionError('');
    try {
      await updateOrganizationMemberRole(session.accessToken, activeOrganization.id, memberId, role);
      toast('success', `Role updated to ${role}`);
      await handleLoadMembers();
    } catch (err: any) {
      setMemberActionError(err?.response?.data?.message || err?.message || 'Failed to update role.');
    }
  }

  async function handleRemoveMember(memberId: string) {
    if (!session || !activeOrganization) return;
    const member = orgMembers.find((m) => m.tenantOrganizationMemberId === memberId);
    const who = member?.email || member?.displayName || 'this member';
    const confirmed = await confirm({
      title: 'Remove member?',
      message: `${who} will immediately lose access to all projects and releases in ${activeOrganization.name}. This cannot be undone.`,
      confirmLabel: 'Remove member',
      danger: true
    });
    if (!confirmed) return;

    setMemberActionError('');
    try {
      await removeOrganizationMember(session.accessToken, activeOrganization.id, memberId);
      toast('success', `Removed ${who} from ${activeOrganization.name}`);
      await handleLoadMembers();
    } catch (err: any) {
      setMemberActionError(err?.response?.data?.message || err?.message || 'Failed to remove member.');
    }
  }

  async function handleRevokeInvite(inviteId: string) {
    if (!session || !activeOrganization) return;
    const confirmed = await confirm({
      title: 'Revoke invite?',
      message: 'The invite link will stop working immediately. This cannot be undone.',
      confirmLabel: 'Revoke invite',
      danger: true
    });
    if (!confirmed) return;

    setMemberActionError('');
    try {
      await revokeOrganizationInvite(session.accessToken, activeOrganization.id, inviteId);
      toast('success', 'Invite revoked');
      await handleLoadMembers();
    } catch (err: any) {
      setMemberActionError(err?.response?.data?.message || err?.message || 'Failed to revoke invite.');
    }
  }

  const roleOrder: Record<string, number> = { owner: 0, admin: 1, member: 2, viewer: 3 };
  const sortedMembers = [...orgMembers].sort(
    (a, b) => (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9)
  );

  const myMembership = orgMembers.find(m => m.tenantUserId === session?.user?.tenantUserId);
  const myOrgRole = myMembership?.role || session?.user?.role || '';
  const iAmOwner = myOrgRole === 'owner';
  const iAmAdmin = myOrgRole === 'admin';
  const canManage = iAmOwner || iAmAdmin;
  const ownerCount = orgMembers.filter(m => m.role === 'owner' && m.isActive).length;

  return (
    <div className="workspaceAccessPage">
      <div className="workspaceAccessSummary" aria-label="Workspace access summary">
        <span><strong>{orgMembers.length}</strong> members</span>
        <span><strong>{orgMembers.filter((member) => ['owner', 'admin'].includes(member.role)).length}</strong> administrators</span>
        <span><strong>{orgInvites.length}</strong> pending invites</span>
        <span className="workspaceAccessRole">Your role: <strong>{myOrgRole || 'unknown'}</strong></span>
      </div>
      {memberActionError && (
        <div className="inlineErrorNotice">
          <span>{memberActionError}</span>
          <button type="button" className="iconButton" onClick={() => setMemberActionError('')}><X size={13} /></button>
        </div>
      )}

      <SectionCard
        label="Team"
        title="Members"
        description="All active members and their roles within this organization."
      >
        {membersLoading ? (
          <div className="workspaceAccessLoading">Loading members…</div>
        ) : membersLoadError ? (
          <EmptyState
            icon={<Users size={20} />}
            title="Failed to load members"
            description="Could not reach the server. Check your connection and try again."
            action={<button type="button" className="secondaryButton" onClick={() => void handleLoadMembers()}>Retry</button>}
          />
        ) : sortedMembers.length > 0 ? (
          <div className="tableBody">
            {sortedMembers.map((member) => {
              const isSelf = member.tenantUserId === session?.user?.tenantUserId;
              const targetIsOwner = member.role === 'owner';
              const targetIsAdmin = member.role === 'admin';
              const resolvedDisplayName = member.displayName || (isSelf ? session?.user?.displayName : null) || null;
              const resolvedEmail = member.email || (isSelf ? session?.user?.email : null) || null;
              const displayLabel = resolvedDisplayName || resolvedEmail || member.tenantUserId.slice(0, 8) + '…';
              const initials = initialsFrom(resolvedDisplayName || resolvedEmail || member.tenantUserId);
              const isLastOwner = targetIsOwner && ownerCount <= 1;

              const showRoleDropdown = canManage && !isSelf && !targetIsOwner && (iAmOwner || !targetIsAdmin);

              const roleOptions: Array<{ value: string; label: string }> = iAmOwner
                ? [{ value: 'admin', label: 'Admin' }, { value: 'member', label: 'Member' }, { value: 'viewer', label: 'Viewer' }]
                : [{ value: 'member', label: 'Member' }, { value: 'viewer', label: 'Viewer' }];

              const showRemoveButton = !isLastOwner && (
                isSelf
                  ? true
                  : !targetIsOwner && (iAmOwner || (iAmAdmin && !targetIsAdmin))
              );

              return (
                <div key={member.tenantOrganizationMemberId} className="memberTableRow">
                  <div className="memberTableAvatar">
                    {initials}
                  </div>
                  <div className="memberTableInfo">
                    <strong>{displayLabel}{isSelf ? <small>you</small> : null}</strong>
                    {resolvedEmail && resolvedDisplayName && <span>{resolvedEmail}</span>}
                  </div>
                  <div className="memberTableActions">
                    {showRoleDropdown ? (
                      <select
                        className="inlineSelect"
                        value={member.role}
                        onChange={(e) => void handleUpdateMemberRole(member.tenantOrganizationMemberId, e.target.value as 'admin' | 'member' | 'viewer')}
                      >
                        {roleOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span className={`memberRole role-${member.role}`}>{member.role}</span>
                    )}
                    {showRemoveButton ? (
                      <button
                        type="button"
                        className="iconButton memberRemoveButton"
                        title={isSelf ? 'Leave organization' : `Remove ${displayLabel}`}
                        onClick={() => {
                          void handleRemoveMember(member.tenantOrganizationMemberId);
                        }}
                      >
                        <UserMinus size={14} />
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={<Users size={20} />}
            title="No members yet"
            description="This organization has no active members."
          />
        )}
      </SectionCard>

      {canManage && (
        <SectionCard
          label="Invites"
          title="Pending invites"
          description="Active invite links that haven't been accepted yet. Links expire after 7 days."
        >
          {orgInvites.length > 0 ? (
            <div className="tableBody">
              {orgInvites.map((invite) => {
                const expiresDate = new Date(invite.expiresAt);
                const isExpiringSoon = expiresDate.getTime() - Date.now() < 86400000 * 2;
                return (
                  <div key={invite.tenantOrganizationInviteId} className="memberTableRow">
                    <div className="memberTableAvatar memberTableAvatar--invite">
                      <UserPlus size={14} />
                    </div>
                    <div className="memberTableInfo">
                      <strong>{invite.emailHint || 'Open invite'}</strong>
                      <span>
                        Role: {invite.role} · Expires{' '}
                        <span title={formatAbsoluteDate(invite.expiresAt)}>{formatRelativeDate(invite.expiresAt)}</span>
                        {isExpiringSoon && <em>expiring soon</em>}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="dangerButton"
                      onClick={() => {
                        void handleRevokeInvite(invite.tenantOrganizationInviteId);
                      }}
                    >
                      Revoke
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              compact
              icon={<UserPlus size={16} />}
              title="No pending invites"
              description="Generate an invite link to add a new collaborator to this organization."
            />
          )}
        </SectionCard>
      )}
    </div>
  );
}

export { MembersPage };
